import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { FileText, Plus, MagnifyingGlass, Upload, Eye, Funnel, ArrowLeft, ArrowRight, DownloadSimple, Paperclip, ArrowUp, ArrowDown, GearSix, X, Archive, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { FilterBuilder } from '../components/FilterBuilder';
import { DocumentViewer, getFileIcon, isViewable } from '../components/DocumentViewer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RecordsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [structures, setStructures] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStructure, setSelectedStructure] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadStructureId, setUploadStructureId] = useState('');
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filterConditions, setFilterConditions] = useState([]);
  const [filterLogic, setFilterLogic] = useState('and');
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [appliedLogic, setAppliedLogic] = useState('and');
  const [exporting, setExporting] = useState(false);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [assignUserId, setAssignUserId] = useState('');
  const [branchUsers, setBranchUsers] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [assignMode, setAssignMode] = useState('selected');

  // Sort state: array of {field, dir} for sequential multi-sort
  const [sortSpec, setSortSpec] = useState([]);
  // Column visibility
  const [hiddenColumns, setHiddenColumns] = useState(new Set());
  const [isColumnDialogOpen, setIsColumnDialogOpen] = useState(false);
  // Document popover
  const [docPopoverRecordId, setDocPopoverRecordId] = useState(null);
  const [docPopoverDocs, setDocPopoverDocs] = useState([]);
  const [docPopoverLoading, setDocPopoverLoading] = useState(false);
  const docPopoverRef = useRef(null);
  // Document preview
  const [previewDoc, setPreviewDoc] = useState(null);

  // Drag-and-drop CSV import (super_admin / branch_admin only)
  const isAdmin = user?.role === 'super_admin' || user?.role === 'branch_admin';
  const [isDragging, setIsDragging] = useState(false);
  const [dragUploading, setDragUploading] = useState(false);
  const dragCounter = useRef(0);

  // Initialize structures and default to first schema
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [structuresRes, statusesRes, branchesRes] = await Promise.all([
          axios.get(`${API_URL}/api/data-structures`),
          axios.get(`${API_URL}/api/statuses`),
          axios.get(`${API_URL}/api/branches`)
        ]);
        setStructures(structuresRes.data);
        setStatuses(statusesRes.data);
        setBranches(branchesRes.data);
        // Default to first schema
        if (structuresRes.data.length > 0 && !selectedStructure) {
          setSelectedStructure(structuresRes.data[0].id);
        }
      } catch (e) {
        console.error('Failed to fetch metadata');
      }
    };
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user?.role === 'super_admin' || user?.role === 'branch_admin') {
      axios.get(`${API_URL}/api/users`).then(res => setBranchUsers(res.data)).catch(() => {});
    }
  }, [user?.role]);

  const fetchRecords = useCallback(async () => {
    if (!selectedStructure) return;
    try {
      const params = new URLSearchParams();
      params.append('structure_id', selectedStructure);
      if (selectedStatus) params.append('status', selectedStatus);
      if (selectedBranch) params.append('branch_id', selectedBranch);
      if (searchTerm) params.append('search', searchTerm);
      if (appliedFilters.length > 0) {
        params.append('filters', JSON.stringify(appliedFilters));
        params.append('filter_logic', appliedLogic);
      }
      // Send primary sort to backend
      if (sortSpec.length > 0) {
        params.append('sort_by', sortSpec[0].field);
        params.append('sort_dir', sortSpec[0].dir);
      }
      params.append('page', page);
      params.append('limit', 20);

      const { data } = await axios.get(`${API_URL}/api/records?${params}`);
      setRecords(data.records);
      setTotalPages(data.pages);
    } catch (e) {
      toast.error('Failed to fetch records');
    } finally {
      setLoading(false);
    }
  }, [selectedStructure, selectedStatus, selectedBranch, searchTerm, appliedFilters, appliedLogic, sortSpec, page]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // Close doc popover on outside click
  useEffect(() => {
    const handler = (e) => {
      if (docPopoverRef.current && !docPopoverRef.current.contains(e.target)) {
        setDocPopoverRecordId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile || !uploadStructureId) {
      toast.error('Please select a file and data structure');
      return;
    }
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('structure_id', uploadStructureId);
    try {
      const { data } = await axios.post(`${API_URL}/api/records/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Uploaded ${data.count} records`);
      setIsUploadDialogOpen(false);
      setUploadFile(null);
      setUploadStructureId('');
      fetchRecords();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to upload records');
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedRecords.length === 0 || !bulkStatus) {
      toast.error('Please select records and a status');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/records/bulk-update`, {
        record_ids: selectedRecords,
        status: bulkStatus
      });
      toast.success(`Updated ${selectedRecords.length} records`);
      setIsBulkDialogOpen(false);
      setSelectedRecords([]);
      setBulkStatus('');
      fetchRecords();
    } catch (e) {
      toast.error('Failed to update records');
    }
  };

  const handleAssignRecords = async () => {
    if (!assignUserId) {
      toast.error('Please select a user');
      return;
    }
    setAssigning(true);
    try {
      if (assignMode === 'selected' && selectedRecords.length > 0) {
        await axios.post(`${API_URL}/api/records/bulk-update`, {
          record_ids: selectedRecords,
          assigned_to: assignUserId
        });
        toast.success(`Assigned ${selectedRecords.length} records`);
        setSelectedRecords([]);
      } else {
        const payload = { assigned_to: assignUserId, unassigned_only: true };
        if (selectedStructure) payload.structure_id = selectedStructure;
        if (appliedFilters.length > 0) {
          payload.filters = JSON.stringify(appliedFilters);
          payload.filter_logic = appliedLogic;
        }
        const { data } = await axios.post(`${API_URL}/api/records/bulk-assign`, payload);
        toast.success(data.message);
      }
      setIsAssignDialogOpen(false);
      setAssignUserId('');
      fetchRecords();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to assign records');
    } finally {
      setAssigning(false);
    }
  };

  const toggleRecordSelection = (recordId) => {
    setSelectedRecords(prev =>
      prev.includes(recordId)
        ? prev.filter(id => id !== recordId)
        : [...prev, recordId]
    );
  };

  const toggleAllRecords = () => {
    if (selectedRecords.length === records.length) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(records.map(r => r.id));
    }
  };

  const getStatusColor = (statusName) => {
    const status = statuses.find(s => s.name === statusName);
    return status?.color || '#666';
  };

  const handleArchiveRecord = async (recordId) => {
    if (!window.confirm('Archive this record? It can be restored later.')) return;
    try {
      await axios.post(`${API_URL}/api/records/${recordId}/archive`);
      toast.success('Record archived');
      fetchRecords();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to archive record');
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (!window.confirm('Permanently delete this record? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/records/${recordId}`);
      toast.success('Record permanently deleted');
      fetchRecords();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete record');
    }
  };

  const handleBulkArchive = async () => {
    if (selectedRecords.length === 0) return;
    if (!window.confirm(`Archive ${selectedRecords.length} records?`)) return;
    try {
      await axios.post(`${API_URL}/api/records/bulk-archive`, selectedRecords);
      toast.success(`Archived ${selectedRecords.length} records`);
      setSelectedRecords([]);
      fetchRecords();
    } catch (e) {
      toast.error('Failed to archive records');
    }
  };

  const getBranchName = (branchId) => {
    const branch = branches.find(b => b.id === branchId);
    return branch?.name || '-';
  };

  const getFilterFields = () => {
    const allFields = new Set();
    const s = structures.find(st => st.id === selectedStructure);
    if (s) (s.fields || []).filter(f => f.visible !== false).forEach(f => allFields.add(f.name));
    allFields.add('status');
    return Array.from(allFields);
  };

  const handleApplyFilters = () => {
    const valid = filterConditions.filter(c => c.field && c.value);
    setAppliedFilters(valid);
    setAppliedLogic(filterLogic);
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilterConditions([]);
    setAppliedFilters([]);
    setAppliedLogic('and');
    setPage(1);
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (selectedStructure) params.append('structure_id', selectedStructure);
      if (selectedStatus) params.append('status', selectedStatus);
      if (selectedBranch) params.append('branch_id', selectedBranch);
      if (searchTerm) params.append('search', searchTerm);
      if (appliedFilters.length > 0) {
        params.append('filters', JSON.stringify(appliedFilters));
        params.append('filter_logic', appliedLogic);
      }
      const response = await axios.get(`${API_URL}/api/records/export/csv?${params}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'records_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Records exported');
    } catch (e) {
      toast.error('Failed to export records');
    } finally {
      setExporting(false);
    }
  };

  // Drag-and-drop CSV import
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAdmin || !selectedStructure) return;
    dragCounter.current++;
    if (e.dataTransfer.items?.length > 0) setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) setIsDragging(false);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;
    if (!isAdmin || !selectedStructure) return;

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    const ext = file.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx', 'xls'].includes(ext)) {
      toast.error('Please drop a CSV or Excel file');
      return;
    }

    setDragUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('structure_id', selectedStructure);
    try {
      const { data } = await axios.post(`${API_URL}/api/records/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Imported ${data.count} records from ${file.name}`);
      fetchRecords();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to import file');
    } finally {
      setDragUploading(false);
    }
  };

  // Sort helpers
  const handleHeaderSort = (fieldName) => {
    setSortSpec(prev => {
      const existing = prev.findIndex(s => s.field === fieldName);
      if (existing >= 0) {
        // Toggle direction or remove
        const item = prev[existing];
        if (item.dir === 'asc') {
          const updated = [...prev];
          updated[existing] = { field: fieldName, dir: 'desc' };
          return updated;
        } else {
          // Remove this sort
          return prev.filter((_, i) => i !== existing);
        }
      } else {
        return [...prev, { field: fieldName, dir: 'asc' }];
      }
    });
    setPage(1);
  };

  const getSortIndicator = (fieldName) => {
    const idx = sortSpec.findIndex(s => s.field === fieldName);
    if (idx < 0) return null;
    const s = sortSpec[idx];
    return (
      <span className="inline-flex items-center ml-1">
        {s.dir === 'asc' ? <ArrowUp size={12} weight="bold" /> : <ArrowDown size={12} weight="bold" />}
        {sortSpec.length > 1 && <span className="text-[10px] ml-0.5 opacity-70">{idx + 1}</span>}
      </span>
    );
  };

  // Paperclip: fetch documents for a record
  const handlePaperclipClick = async (recordId, e) => {
    e.stopPropagation();
    if (docPopoverRecordId === recordId) {
      setDocPopoverRecordId(null);
      return;
    }
    setDocPopoverRecordId(recordId);
    setDocPopoverLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/documents/${recordId}`);
      setDocPopoverDocs(data);
    } catch {
      setDocPopoverDocs([]);
    } finally {
      setDocPopoverLoading(false);
    }
  };

  // Column helpers
  const activeStructure = structures.find(s => s.id === selectedStructure);
  const allSchemaFields = (activeStructure?.fields || []).filter(f => f.visible !== false);
  const visibleFields = allSchemaFields.filter(f => !hiddenColumns.has(f.name));

  const toggleColumn = (fieldName) => {
    setHiddenColumns(prev => {
      const next = new Set(prev);
      if (next.has(fieldName)) {
        next.delete(fieldName);
      } else {
        next.add(fieldName);
      }
      return next;
    });
  };

  // Apply secondary sorting client-side (backend sorts by primary)
  const sortedRecords = React.useMemo(() => {
    if (sortSpec.length <= 1) return records;
    const secondarySorts = sortSpec.slice(1);
    return [...records].sort((a, b) => {
      for (const s of secondarySorts) {
        const aVal = s.field === 'status' ? a.status : (s.field === 'created_at' ? a.created_at : String(a.data?.[s.field] ?? ''));
        const bVal = s.field === 'status' ? b.status : (s.field === 'created_at' ? b.created_at : String(b.data?.[s.field] ?? ''));
        const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
        if (cmp !== 0) return s.dir === 'asc' ? cmp : -cmp;
      }
      return 0;
    });
  }, [records, sortSpec]);

  if (loading && structures.length === 0) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-32 skeleton"></div>
        <div className="h-96 skeleton rounded-md"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Records</h1>
          <p className="text-muted-foreground">Manage customer records</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={handleExportCSV} disabled={exporting} data-testid="export-csv-btn">
            <DownloadSimple size={18} className="mr-2" />
            {exporting ? 'Exporting...' : 'Export CSV'}
          </Button>
          <Button variant="outline" onClick={() => setIsUploadDialogOpen(true)} data-testid="upload-records-btn">
            <Upload size={18} className="mr-2" />
            Upload
          </Button>
          {selectedRecords.length > 0 && (
            <>
              <Button variant="outline" onClick={() => setIsBulkDialogOpen(true)} data-testid="bulk-update-btn">
                Update {selectedRecords.length} selected
              </Button>
              {user?.role === 'super_admin' && (
                <Button variant="outline" onClick={handleBulkArchive} data-testid="bulk-archive-btn">
                  <Archive size={18} className="mr-1" />
                  Archive {selectedRecords.length}
                </Button>
              )}
              {(user?.role === 'super_admin' || user?.role === 'branch_admin') && (
                <Button variant="outline" onClick={() => { setAssignMode('selected'); setIsAssignDialogOpen(true); }} data-testid="assign-selected-btn">
                  Assign {selectedRecords.length} selected
                </Button>
              )}
            </>
          )}
          {(user?.role === 'super_admin' || user?.role === 'branch_admin') && selectedRecords.length === 0 && (
            <Button variant="outline" onClick={() => { setAssignMode('filter'); setIsAssignDialogOpen(true); }} data-testid="assign-by-filter-btn">
              Assign Unassigned
            </Button>
          )}
          <Link to="/records/new">
            <Button data-testid="new-record-btn">
              <Plus size={18} className="mr-2" />
              New Record
            </Button>
          </Link>
        </div>
      </div>

      {/* Schema Tabs */}
      {structures.length > 0 && (
        <div className="flex gap-1 border-b overflow-x-auto pb-0" data-testid="schema-tabs">
          {structures.map(s => (
            <button
              key={s.id}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${selectedStructure === s.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'}`}
              onClick={() => { setSelectedStructure(s.id); setPage(1); setSortSpec([]); setHiddenColumns(new Set()); setFilterConditions([]); setAppliedFilters([]); }}
              data-testid={`schema-tab-${s.id}`}
            >
              {s.name}
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-muted-foreground mb-1 block">Search</Label>
              <div className="relative">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search records..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-records"
                />
              </div>
            </div>
            <div className="w-48">
              <Label className="text-xs text-muted-foreground mb-1 block">Status</Label>
              <Select value={selectedStatus || "all"} onValueChange={(v) => setSelectedStatus(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-status">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {statuses.map(s => (
                    <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {user?.role === 'super_admin' && (
              <div className="w-48">
                <Label className="text-xs text-muted-foreground mb-1 block">Branch</Label>
                <Select value={selectedBranch || "all"} onValueChange={(v) => setSelectedBranch(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="filter-branch">
                    <SelectValue placeholder="All branches" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All branches</SelectItem>
                    {branches.map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 pt-2">
            <Button
              variant={showAdvancedFilters ? "default" : "outline"}
              size="sm"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              data-testid="toggle-advanced-filters"
            >
              <Funnel size={14} className="mr-1" />
              {showAdvancedFilters ? 'Hide' : 'Advanced'} Filters
              {appliedFilters.length > 0 && (
                <span className="ml-1 bg-primary-foreground text-primary rounded-full w-5 h-5 flex items-center justify-center text-xs">
                  {appliedFilters.length}
                </span>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsColumnDialogOpen(true)}
              data-testid="column-settings-btn"
            >
              <GearSix size={14} className="mr-1" />
              Columns
              {hiddenColumns.size > 0 && (
                <span className="ml-1 text-xs opacity-70">({allSchemaFields.length - hiddenColumns.size}/{allSchemaFields.length})</span>
              )}
            </Button>
            {sortSpec.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setSortSpec([])} className="text-xs text-muted-foreground">
                <X size={12} className="mr-1" /> Clear sort
              </Button>
            )}
          </div>
          {showAdvancedFilters && (
            <div className="pt-3 border-t mt-3">
              <FilterBuilder
                conditions={filterConditions}
                setConditions={setFilterConditions}
                logic={filterLogic}
                setLogic={setFilterLogic}
                fields={getFilterFields()}
                onApply={handleApplyFilters}
                onClear={handleClearFilters}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Records Table */}
      <Card
        className="relative"
        onDragEnter={isAdmin ? handleDragEnter : undefined}
        onDragLeave={isAdmin ? handleDragLeave : undefined}
        onDragOver={isAdmin ? handleDragOver : undefined}
        onDrop={isAdmin ? handleDrop : undefined}
        data-testid="records-table-card"
      >
        {/* Drop overlay */}
        {isDragging && (
          <div className="absolute inset-0 z-30 flex items-center justify-center bg-primary/5 border-2 border-dashed border-primary rounded-lg pointer-events-none" data-testid="drop-overlay">
            <div className="text-center">
              <Upload size={40} className="mx-auto mb-2 text-primary animate-bounce" />
              <p className="text-sm font-semibold text-primary">Drop CSV / Excel to import</p>
              <p className="text-xs text-muted-foreground mt-1">
                Records will be added to <span className="font-medium">{activeStructure?.name || 'current schema'}</span>
              </p>
            </div>
          </div>
        )}
        {/* Upload progress overlay */}
        {dragUploading && (
          <div className="absolute inset-0 z-30 flex items-center justify-center bg-background/80 rounded-lg" data-testid="upload-progress-overlay">
            <div className="text-center">
              <div className="spinner w-8 h-8 mx-auto mb-2"></div>
              <p className="text-sm font-medium">Importing records...</p>
            </div>
          </div>
        )}
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={selectedRecords.length === records.length && records.length > 0}
                    onCheckedChange={toggleAllRecords}
                    data-testid="select-all-records"
                  />
                </TableHead>
                {visibleFields.slice(0, 6).map(f => (
                  <TableHead
                    key={f.name}
                    className="cursor-pointer select-none hover:bg-secondary/50 transition-colors"
                    onClick={() => handleHeaderSort(f.name)}
                    data-testid={`sort-header-${f.name}`}
                  >
                    <span className="inline-flex items-center">
                      {f.name}
                      {getSortIndicator(f.name)}
                    </span>
                  </TableHead>
                ))}
                <TableHead
                  className="cursor-pointer select-none hover:bg-secondary/50 transition-colors"
                  onClick={() => handleHeaderSort('status')}
                  data-testid="sort-header-status"
                >
                  <span className="inline-flex items-center">
                    Status{getSortIndicator('status')}
                  </span>
                </TableHead>
                <TableHead>Branch</TableHead>
                <TableHead>Assigned To</TableHead>
                <TableHead
                  className="cursor-pointer select-none hover:bg-secondary/50 transition-colors"
                  onClick={() => handleHeaderSort('created_at')}
                  data-testid="sort-header-created"
                >
                  <span className="inline-flex items-center">
                    Created{getSortIndicator('created_at')}
                  </span>
                </TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedRecords.length > 0 ? (
                sortedRecords.map(record => (
                  <TableRow key={record.id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedRecords.includes(record.id)}
                        onCheckedChange={() => toggleRecordSelection(record.id)}
                        data-testid={`select-record-${record.id}`}
                      />
                    </TableCell>
                    {visibleFields.slice(0, 6).map(f => (
                      <TableCell key={f.name} className="text-sm max-w-[180px] truncate">
                        {Array.isArray(record.data?.[f.name]) ? record.data[f.name].join(', ') : String(record.data?.[f.name] ?? '-')}
                      </TableCell>
                    ))}
                    <TableCell>
                      <Badge
                        variant="outline"
                        style={{ borderColor: getStatusColor(record.status), color: getStatusColor(record.status) }}
                      >
                        {record.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{getBranchName(record.branch_id)}</TableCell>
                    <TableCell>
                      {record.assigned_to_name ? (
                        <Badge variant="secondary" className="text-xs">{record.assigned_to_name}</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">Unassigned</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">{new Date(record.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-0.5 relative">
                        {record.document_count > 0 && (
                          <div className="relative">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={(e) => handlePaperclipClick(record.id, e)}
                              data-testid={`paperclip-${record.id}`}
                            >
                              <Paperclip size={16} />
                              <span className="absolute -top-0.5 -right-0.5 bg-primary text-primary-foreground text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                                {record.document_count}
                              </span>
                            </Button>
                            {/* Document popover */}
                            {docPopoverRecordId === record.id && (
                              <div
                                ref={docPopoverRef}
                                className="absolute right-0 top-full mt-1 z-50 w-64 bg-popover border rounded-lg shadow-lg p-2 animate-fade-in"
                                data-testid={`doc-popover-${record.id}`}
                              >
                                <p className="text-xs font-medium text-muted-foreground px-2 pb-1 border-b mb-1">
                                  Documents ({record.document_count})
                                </p>
                                {docPopoverLoading ? (
                                  <div className="p-3 text-center text-xs text-muted-foreground">Loading...</div>
                                ) : docPopoverDocs.length > 0 ? (
                                  <div className="max-h-48 overflow-y-auto space-y-0.5">
                                    {docPopoverDocs.map(doc => (
                                      <button
                                        key={doc.id}
                                        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left hover:bg-secondary/60 transition-colors"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setDocPopoverRecordId(null);
                                          if (isViewable(doc.original_filename)) {
                                            setPreviewDoc(doc);
                                          } else {
                                            window.open(`${API_URL}/api/documents/download/${doc.id}`, '_blank');
                                          }
                                        }}
                                        data-testid={`doc-item-${doc.id}`}
                                      >
                                        {(() => { const Icon = getFileIcon(doc.original_filename); return <Icon size={16} className="shrink-0 text-muted-foreground" />; })()}
                                        <div className="min-w-0 flex-1">
                                          <p className="text-xs font-medium truncate">{doc.original_filename}</p>
                                          <p className="text-[10px] text-muted-foreground">{new Date(doc.uploaded_at).toLocaleDateString()}</p>
                                        </div>
                                        {isViewable(doc.original_filename) && <Eye size={12} className="text-muted-foreground shrink-0" />}
                                      </button>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="p-3 text-center text-xs text-muted-foreground">No documents</div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => navigate(`/records/${record.id}`)}
                          data-testid={`view-record-${record.id}`}
                        >
                          <Eye size={16} />
                        </Button>
                        {user?.role === 'super_admin' && (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-amber-600"
                              onClick={() => handleArchiveRecord(record.id)}
                              title="Archive"
                              data-testid={`archive-record-${record.id}`}
                            >
                              <Archive size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              onClick={() => handleDeleteRecord(record.id)}
                              title="Delete permanently"
                              data-testid={`delete-record-${record.id}`}
                            >
                              <Trash size={16} />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={20} className="text-center py-12">
                    <FileText size={32} className="mx-auto mb-2 opacity-50" />
                    <p className="text-muted-foreground">No records found</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {page} of {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ArrowLeft size={16} className="mr-1" /> Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next <ArrowRight size={16} className="ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Records</DialogTitle>
            <DialogDescription>Upload a CSV or Excel file to import records</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4">
            <div className="space-y-2">
              <Label>Data Structure</Label>
              <Select value={uploadStructureId} onValueChange={setUploadStructureId}>
                <SelectTrigger data-testid="upload-structure-select">
                  <SelectValue placeholder="Select structure" />
                </SelectTrigger>
                <SelectContent>
                  {structures.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>File</Label>
              <Input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => setUploadFile(e.target.files[0])}
                data-testid="upload-file-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsUploadDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="confirm-upload">Upload</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Update Dialog */}
      <Dialog open={isBulkDialogOpen} onOpenChange={setIsBulkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Update</DialogTitle>
            <DialogDescription>Update status for {selectedRecords.length} selected records</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>New Status</Label>
              <Select value={bulkStatus} onValueChange={setBulkStatus}>
                <SelectTrigger data-testid="bulk-status-select">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map(s => (
                    <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setIsBulkDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleBulkUpdate} data-testid="confirm-bulk-update">Update Records</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Records Dialog */}
      <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {assignMode === 'selected' ? `Assign ${selectedRecords.length} Records` : 'Assign Unassigned Records'}
            </DialogTitle>
            <DialogDescription>
              {assignMode === 'selected'
                ? 'Assign the selected records to a team member'
                : 'Assign all unassigned records matching current filters to a team member'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {assignMode === 'filter' && appliedFilters.length > 0 && (
              <div className="p-3 bg-secondary/50 rounded-md">
                <p className="text-xs text-muted-foreground mb-1">Active filter criteria:</p>
                {appliedFilters.map((f, i) => (
                  <Badge key={i} variant="outline" className="text-xs mr-1 mb-1">
                    {f.field} {f.operator} "{f.value}"
                  </Badge>
                ))}
              </div>
            )}
            <div className="space-y-2">
              <Label>Assign To</Label>
              <Select value={assignUserId || "none"} onValueChange={(v) => setAssignUserId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="assign-user-select">
                  <SelectValue placeholder="Select user" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select a user</SelectItem>
                  {branchUsers.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name} ({u.role.replace('_', ' ')})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setIsAssignDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAssignRecords} disabled={assigning || !assignUserId} data-testid="confirm-assign">
                {assigning ? 'Assigning...' : 'Assign Records'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Column Settings Dialog */}
      <Dialog open={isColumnDialogOpen} onOpenChange={setIsColumnDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Column Settings</DialogTitle>
            <DialogDescription>Choose which columns to display in the table</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {allSchemaFields.map(f => (
              <div
                key={f.name}
                className="flex items-center gap-3 p-2 rounded-md hover:bg-secondary/40 transition-colors"
              >
                <Checkbox
                  checked={!hiddenColumns.has(f.name)}
                  onCheckedChange={() => toggleColumn(f.name)}
                  data-testid={`col-toggle-${f.name}`}
                />
                <Label className="text-sm cursor-pointer flex-1">{f.name}</Label>
                <Badge variant="outline" className="text-[10px]">{f.type || 'text'}</Badge>
              </div>
            ))}
          </div>
          <div className="flex justify-between pt-2">
            <Button variant="ghost" size="sm" onClick={() => setHiddenColumns(new Set())} data-testid="col-show-all">
              Show All
            </Button>
            <Button size="sm" onClick={() => setIsColumnDialogOpen(false)}>Done</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Preview Modal */}
      {previewDoc && (
        <DocumentViewer document={previewDoc} onClose={() => setPreviewDoc(null)} canDownload={user?.role === 'super_admin' || user?.role === 'branch_admin'} />
      )}
    </div>
  );
};

export default RecordsPage;
